import {
  Alert,
  AlertIcon,
  Button,
  HStack,
  Link,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useState } from "react";
import {
  Item,
  SearchSelect,
} from "src/components/Inputs/SearchSelect/SearchSelect";
import { ResizableDrawer } from "src/components/Layout/ResizableDrawer";
import {
  getAvailableOfferingOptions,
  groupOfferingOptionsByCategory,
  offeringKey,
  type OfferingOption,
} from "./offerings";

type Props = {
  isOpen: boolean;
  options: OfferingOption[];
  existingOfferingKeys: Set<string>;
  totalNumSlots: number;
  onClose: () => void;
  onAddOffering: (optionIds: string[]) => void;
  onGoToOfferingCategories: () => void;
};

const emptySelection = (totalNumSlots: number): Array<Item | null> =>
  Array.from({ length: totalNumSlots }, () => null);

export const AddOfferingDrawer: React.FC<Props> = ({
  isOpen,
  options,
  existingOfferingKeys,
  totalNumSlots,
  onClose,
  onAddOffering,
  onGoToOfferingCategories,
}) => {
  const [selectedOptions, setSelectedOptions] = useState<Array<Item | null>>(
    emptySelection(totalNumSlots)
  );
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const selectedOptionIds = selectedOptions.map(
    (option) => option?.key ?? null
  );
  const isFirstOptionMissing = selectedOptionIds[0] === null;

  // No offerings with the same options in the same order are allowed.
  const isDuplicateOffering =
    !isFirstOptionMissing &&
    existingOfferingKeys.has(
      offeringKey(selectedOptionIds.filter((optionId) => optionId !== null))
    );
  const showDuplicateOfferingError = hasAttemptedSubmit && isDuplicateOffering;

  const handleClose = () => {
    setSelectedOptions(emptySelection(totalNumSlots));
    setHasAttemptedSubmit(false);
    onClose();
  };

  const handleAddOffering = () => {
    setHasAttemptedSubmit(true);

    if (isFirstOptionMissing || isDuplicateOffering) {
      return;
    }

    onAddOffering(selectedOptionIds.filter((optionId) => optionId !== null));
    handleClose();
  };

  return (
    <ResizableDrawer
      isOpen={isOpen}
      onClose={handleClose}
      header="Add offering"
      contentProps={{ bg: "white" }}
      footer={
        <HStack spacing={2} w="full">
          <Button
            variant="outline"
            colorScheme="gray"
            flex={1}
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button colorScheme="primary" flex={1} onClick={handleAddOffering}>
            Add offering
          </Button>
        </HStack>
      }
    >
      <VStack spacing={6} align="stretch">
        <Text fontSize="sm" color="gray.600">
          Select from the list of available offering categories - if the
          category is not available,{" "}
          <Link
            as="button"
            type="button"
            color="primary.500"
            textDecoration="underline"
            onClick={() => {
              handleClose();
              onGoToOfferingCategories();
            }}
          >
            create it first
          </Link>{" "}
          and then come back to select it.
        </Text>
        {Array.from({ length: totalNumSlots }, (_, index) => {
          const availableOptions = getAvailableOfferingOptions({
            options,
            selectedOptionIds,
            slotIndex: index,
            totalNumSlots,
            existingOfferingKeys,
          });

          return (
            <SearchSelect
              key={`${index}-${selectedOptions[index]?.key ?? ""}`}
              label={
                `Select offering option ${index + 1}` +
                (index === 0 ? "" : " (optional)")
              }
              items={groupOfferingOptionsByCategory(availableOptions)}
              selectedItem={selectedOptions[index] ?? null}
              disabled={index > 0 && selectedOptions[index - 1] === null}
              error={
                hasAttemptedSubmit && index === 0 && isFirstOptionMissing
                  ? "The first option is required"
                  : undefined
              }
              onChange={(item) => {
                setSelectedOptions((current) => {
                  const next = [...current];
                  next[index] = item;
                  if (item === null) {
                    for (
                      let laterIndex = index + 1;
                      laterIndex < next.length;
                      laterIndex++
                    ) {
                      next[laterIndex] = null;
                    }
                  }
                  return next;
                });
              }}
              placeholder="Select an option"
              variant="unlocked"
            />
          );
        })}
        {showDuplicateOfferingError && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Text fontSize="sm">This offering already exists</Text>
          </Alert>
        )}
      </VStack>
    </ResizableDrawer>
  );
};
